/**
 * The decision behind every `PermissionGate` (renders a control) and `PermissionGuard`
 * (renders a route).
 *
 * It lives here rather than in either component because both must answer the question the same
 * way. A screen whose button is hidden but whose route still opens is not gated — it is gated in
 * one of the two places someone can arrive from, which is the shape of the bug this exists to
 * close. One function, one answer, whichever door is used.
 */

interface PermissionGateInput {
  /** The capability check itself — `can(action, subject, scope)`, already resolved. */
  permitted: boolean;
  /** What is gated belongs to a client, so a GFG staff session must not reach it directly. */
  clientOnly: boolean;
  /** GFG staff session: a membership at level ALL, resolved server-side. */
  isGfg: boolean;
}

/**
 * True when the caller gets through the gate: `PermissionGate` renders its control, or
 * `PermissionGuard` opens its route.
 *
 * `clientOnly` is a SECOND condition, never a replacement for the capability check. GFG admins
 * genuinely hold `create CAMPAIGN` at scope "all" — the caps are correct and must stay that way,
 * because they are what lets those admins act once they are impersonating. What the caps cannot
 * express is that the action belongs to a client and is performed BY impersonating one, never from
 * a staff console. That is this flag.
 *
 * Impersonation needs nothing extra to satisfy it. Clerk's actor token makes the session the
 * target user, so their memberships — and therefore `isGfg` — are the client's, and what was
 * hidden comes back on its own for as long as the impersonation lasts.
 *
 * Note this is a UX affordance, not a security boundary: the server still permits these calls, and
 * a client-side check can always be bypassed. It decides what to show, not what is allowed.
 */
export function passesPermissionGate({
  permitted,
  clientOnly,
  isGfg,
}: PermissionGateInput): boolean {
  if (!permitted) return false;
  return !(clientOnly && isGfg);
}
